import { useContext, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Person as PersonIcon,
  SettingsOutlined as SettingsIcon,
  UnfoldMore as UnfoldMoreIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  TeamsInterface,
  TeamsStatus,
  UserProfileRole,
  Workspace,
} from "ts-interfaces";
import { firebaseHelper, workspacesEnabled } from "ui";
import { isTeamPlanActive } from "ui/utils/team/teamRole";
import { AuthContext } from "../auth/Auth";
import { onAnalyticsInitialized, trackEvent } from "../libs/analyticsManager";
import { teamAnalytics } from "../team/teamAnalytics";
import { useLoginState } from "../studioxr/auth/LoginState";
import { useMattoState } from "../MattoState";
import { switchClosesProject, withoutProjectParam } from "../Home/projectRail";
import { useWorkspaceMenu } from "./useWorkspaceMenu";
import { reactivateOwnMembership } from "./reactivateOwnMembership";
import { workspaceColor, workspaceInitials } from "./workspaceAvatar";
import {
  markSwitcherTipSeen,
  readTipSeenLocally,
  shouldShowSwitcherTip,
  useCoveredByModal,
  useSwitcherTipStore,
} from "./switcherTip";
import { useTeamWelcomeStore } from "../team/onboarding/welcomeGate";
import {
  CREATE_TEAM,
  INACTIVE_ADMIN_ROW_SECONDARY,
  INACTIVE_ROW_SECONDARY,
  INACTIVE_ROW_TOOLTIP,
  MANAGE_TEAM,
  PERSONAL_ROW_SECONDARY,
  PLAN_TAG_FREE,
  PLAN_TAG_PRO,
  PLAN_TAG_TEAM,
  SWITCHER_TIP_DISMISS,
  SWITCHER_TIP_TITLE,
  WORKSPACE_LOADING_LABEL,
  switcherTipBody,
  teamRowSecondary,
} from "./workspaceStrings";

// Members as the Members page counts them (useTeamMembers): active + inactive.
const countMembers = async (firmId: string): Promise<number> => {
  const snap = await getDocs(
    query(
      collection(firebaseHelper.db, "teams"),
      where("designFirmID", "==", firmId),
    ),
  );
  return snap.docs.filter((d) => {
    const { status } = d.data() as TeamsInterface;
    return status === TeamsStatus.ACTIVE || status === TeamsStatus.INACTIVE;
  }).length;
};

// The callout waits a beat so it doesn't race the page's own load-time modals.
const TIP_DELAY_MS = 1000;

// Dark, so the card and its arrow stand out against the white top bar.
const TIP_BG = "#18181b";

const tipPaperSx = {
  position: "relative",
  width: 300,
  maxWidth: "calc(100vw - 32px)",
  p: 2,
  borderRadius: "12px",
  boxShadow: "0 16px 40px rgba(0, 0, 0, 0.24), 0 2px 8px rgba(0, 0, 0, 0.12)",
  bgcolor: TIP_BG,
  color: "#fff",
} as const;

// A rotated square peeking above the card, pointing up at the trigger.
const tipArrowSx = {
  position: "absolute",
  top: -6,
  left: 18,
  width: 12,
  height: 12,
  bgcolor: TIP_BG,
  transform: "rotate(45deg)",
} as const;

const menuPaperSx = {
  width: 300,
  maxWidth: "calc(100vw - 24px)",
  maxHeight: "calc(100vh - 96px)",
  mt: 1,
  borderRadius: "12px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 16px 40px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06)",
};

const rowSx = {
  gap: 1.5,
  px: 1,
  py: 0.75,
  borderRadius: "8px",
  // The highlight marks the active workspace, so hovering another row stays lighter.
  "&:hover": { backgroundColor: "#f7f7f8" },
  "&.Mui-selected, &.Mui-selected.Mui-focusVisible": {
    backgroundColor: "#f1f1f3",
  },
  "&.Mui-selected:hover": { backgroundColor: "#ececef" },
};

const actionRowSx = { ...rowSx, py: 1, color: "#3f3f46" };

// Pro in the violet of the "Can edit" RolePill.
const PLAN_TAGS = {
  free: { label: PLAN_TAG_FREE, color: "#3f3f46", bg: "#e9e9ec" },
  pro: { label: PLAN_TAG_PRO, color: "#5b3fa8", bg: "#efe9fb" },
  team: { label: PLAN_TAG_TEAM, color: "#5b3fa8", bg: "#efe9fb" },
} as const;

type Plan = keyof typeof PLAN_TAGS;

const PlanTag = ({ plan }: { plan: Plan }) => {
  const { label, color, bg } = PLAN_TAGS[plan];
  return (
    <Typography
      component="span"
      data-testid="workspace-plan-tag"
      sx={{
        flexShrink: 0,
        px: 0.75,
        py: 0.25,
        borderRadius: "6px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: "16px",
        whiteSpace: "nowrap",
        color,
        bgcolor: bg,
      }}
    >
      {label}
    </Typography>
  );
};

const WorkspaceAvatar = ({
  color,
  label,
  personal = false,
  size = 32,
  sx,
}: {
  color: string;
  /** The team name for its initials; null while it loads. */
  label: string | null;
  /** The person icon instead of initials. */
  personal?: boolean;
  size?: number;
  sx?: object;
}) => (
  <Avatar
    variant="rounded"
    sx={{
      width: size,
      height: size,
      // A string: sx multiplies a bare number by the theme radius.
      borderRadius: `${size / 4}px`,
      bgcolor: color,
      color: "#fff",
      fontSize: size * 0.375,
      fontWeight: 700,
      ...sx,
    }}
  >
    {personal ? (
      <PersonIcon sx={{ fontSize: size * 0.56 }} />
    ) : label ? (
      workspaceInitials(label)
    ) : (
      ""
    )}
  </Avatar>
);

interface WorkspaceSwitcherProps {
  /** Bar's live firm doc (useTeamsProAccess): the active team's, or in Personal
   *  the user's first designer team's. A rename or plan change in any tab shows
   *  at once. May still be the previous team after a switch. */
  activeFirm?: {
    id: string;
    name?: string;
    stripeSubscriptionId?: string | null;
    contractTeam?: boolean | null;
    status?: string | null;
  } | null;
  /** Returns false to stay put (e.g. a board with unsaved changes). */
  onBeforeNavigate?: () => boolean;
}

/** `/ <workspace> ⇕` next to the logo: the one place to switch workspace. */
export const WorkspaceSwitcher = ({
  activeFirm,
  onBeforeNavigate,
}: WorkspaceSwitcherProps) => {
  const { currentUser } = useContext(AuthContext);
  const uid = currentUser?.uid ?? null;
  const permissions = useLoginState((state) => state.permissions);
  const setShowSubscription = useMattoState(
    (state) => state.setShowSubscription,
  );
  const activeScheduleProjectId = useMattoState(
    (state) => state.activeScheduleProjectId,
  );
  const navigate = useNavigate();
  const {
    ctx,
    resolveLabel,
    resolveFirmName,
    isFirmNameLoaded,
    teamPlanActive,
  } = useWorkspaceMenu(uid, permissions);
  // Undefined until this user's profile loads, so a Pro user never sees "Free".
  const personalPro = useLoginState((s) =>
    uid && s.profile?.uid === uid
      ? s.profile?.userRole === UserProfileRole.PRO
      : undefined,
  );
  // The last plan Bar's live doc showed per team: it beats the one-off read
  // once Bar stops listening to that team (an admin-only member in Personal).
  const [livePlans, setLivePlans] = useState<Record<string, boolean>>({});
  const liveFirmId = activeFirm?.id;
  const liveFirmPlan = activeFirm ? isTeamPlanActive(activeFirm) : undefined;
  useEffect(() => {
    if (!liveFirmId || liveFirmPlan === undefined) return;
    setLivePlans((prev) =>
      prev[liveFirmId] === liveFirmPlan
        ? prev
        : { ...prev, [liveFirmId]: liveFirmPlan },
    );
  }, [liveFirmId, liveFirmPlan]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});

  const teamIds = ctx.workspaces.flatMap((w) =>
    w.kind === "team" ? [w.firmId] : [],
  );
  const teamIdsKey = teamIds.join(",");
  // Counted afresh on each open; no live listeners for a menu.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    teamIds.forEach((id) => {
      countMembers(id)
        .then((n) => {
          if (!cancelled) setMemberCounts((prev) => ({ ...prev, [id]: n }));
        })
        .catch((err) =>
          console.warn("[WorkspaceSwitcher] member count failed:", err),
        );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamIdsKey]);

  // The one-time "switch workspaces here" callout for team users.
  const { pathname, search } = useLocation();
  const proAccessResolvedUid = useLoginState((s) => s.proAccessResolvedUid);
  const profileSeen = useLoginState(
    (s) => s.profile?.uid === uid && !!s.profile?.workspaceSwitcherTipSeenAt,
  );
  const sessionSeen = useSwitcherTipStore((s) => !!uid && s.seenUid === uid);
  // The team welcome covers the switcher, and marks the tip seen when it closes.
  const welcomePending = useTeamWelcomeStore((s) => !!uid && s.pendingUid === uid);
  // A dialog's backdrop doesn't hide the callout, and a click on it closes the
  // dialog: wait for registered blockers and for any modal over the switcher.
  const dialogOpen = useTeamWelcomeStore((s) =>
    Object.values(s.blockers).some(Boolean),
  );
  const modalOpen = useMattoState((state) => !!state.showSubscription);
  const hasActiveTeam = ctx.workspaces.some((w) => w.kind === "team");
  // One team per user for now: no "Create a team" once the menu lists a team (active or paused).
  const canCreateTeam = !hasActiveTeam && ctx.inactiveTeamIds.size === 0;
  const permissionsSettled =
    !!uid && proAccessResolvedUid === uid && ctx.teamStatusResolved;
  const tipCandidate =
    !welcomePending &&
    !dialogOpen &&
    !modalOpen &&
    shouldShowSwitcherTip({
      enabled: workspacesEnabled(),
      uid,
      permissionsSettled,
      hasActiveTeam,
      seen: profileSeen || sessionSeen || readTipSeenLocally(uid),
      pathname,
    });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const coveredByModal = useCoveredByModal(triggerRef, tipCandidate);
  const tipEligible = tipCandidate && !coveredByModal;
  const [tipShown, setTipShown] = useState(false);
  useEffect(() => {
    if (!tipEligible) {
      setTipShown(false);
      return undefined;
    }
    const timer = setTimeout(() => setTipShown(true), TIP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [tipEligible]);
  const tipVisible = tipEligible && tipShown && !open;
  useEffect(() => {
    if (!tipVisible || !uid) return undefined;
    // trackEvent drops anything sent before analytics init.
    return onAnalyticsInitialized(() => {
      const store = useSwitcherTipStore.getState();
      if (store.viewedUid === uid) return;
      store.markViewed(uid);
      trackEvent("Workspace Switcher - Tip Viewed", {});
    });
  }, [tipVisible, uid]);
  // Seen on this device but the profile stamp never landed: stamp it now so
  // the user's other devices skip the callout too.
  useEffect(() => {
    if (uid && permissionsSettled && !profileSeen && readTipSeenLocally(uid)) {
      markSwitcherTipSeen(uid);
    }
  }, [uid, permissionsSettled, profileSeen]);

  if (!workspacesEnabled() || !uid) return null;

  const { active } = ctx;
  const activeIsTeam = active.kind === "team";
  const liveName =
    active.kind === "team" && activeFirm?.id === active.firmId
      ? activeFirm.name?.trim() || undefined
      : undefined;
  const isLiveTeam = (w: Workspace) =>
    !!liveName && w.kind === "team" && w.id === active.id;
  const labelOf = (w: Workspace) =>
    isLiveTeam(w) ? (liveName as string) : resolveLabel(w);
  const isLoaded = (w: Workspace) =>
    w.kind !== "team" || isLiveTeam(w) || isFirmNameLoaded(w.firmId);
  const nameLoaded = isLoaded(active);
  const activeLabel = labelOf(active);
  // Personal: the user's own plan. A team: Bar's live doc when it's that team's,
  // else the last value it showed, else the one-off read.
  const planOf = (w: Workspace): Plan | null => {
    const live =
      w.kind === "personal"
        ? personalPro
        : activeFirm?.id === w.firmId
          ? isTeamPlanActive(activeFirm)
          : (livePlans[w.firmId] ?? teamPlanActive(w.firmId));
    if (live === undefined) return null;
    if (!live) return "free";
    return w.kind === "team" ? "team" : "pro";
  };
  const close = () => setAnchorEl(null);
  // Reactivating reloads the page: an unsaved board asks first.
  const reactivate = (firmId: string) => {
    close();
    if (onBeforeNavigate && !onBeforeNavigate()) return;
    if (uid) reactivateOwnMembership(uid, firmId);
  };
  // The callout names the active team, or the first team while in Personal.
  const tipTeam = activeIsTeam
    ? active
    : ctx.workspaces.find((w) => w.kind === "team");
  const tipTeamName = tipTeam && isLoaded(tipTeam) ? labelOf(tipTeam) : null;

  // An array, not a fragment: MUI Menu needs direct MenuItem children.
  const items = [
    ...ctx.workspaces.map((w) => {
      const isTeam = w.kind === "team";
      const loaded = isLoaded(w);
      const label = labelOf(w);
      const isActive = w.id === active.id;
      const plan = planOf(w);
      return (
        <MenuItem
          key={w.id}
          selected={isActive}
          onClick={() => {
            if (!isActive) {
              teamAnalytics.workspaceSwitched({
                fromFirmId: active.kind === "team" ? active.firmId : null,
                toFirmId: isTeam ? w.firmId : null,
                trigger: "switcher",
              });
            }
            ctx.switchWorkspace(w.id);
            close();
            // An open project belongs to the workspace being left: close it
            // and land on the new workspace's Projects page. A create page
            // opened from a project keeps its draft and drops the project.
            if (!isActive) {
              if (
                switchClosesProject(pathname, search, activeScheduleProjectId)
              ) {
                navigate("/home/projects");
              } else {
                const draftUrl = withoutProjectParam(pathname, search);
                if (draftUrl) navigate(draftUrl, { replace: true });
              }
            }
          }}
          sx={rowSx}
          data-testid={`workspace-menu-item-${w.kind}`}
        >
          <WorkspaceAvatar
            color={workspaceColor(w)}
            label={loaded ? label : null}
            personal={!isTeam}
          />
          <ListItemText
            primary={loaded ? label : <Skeleton width={96} />}
            secondary={
              isTeam
                ? teamRowSecondary(memberCounts[w.firmId])
                : PERSONAL_ROW_SECONDARY
            }
            primaryTypographyProps={{
              noWrap: true,
              sx: { fontSize: 14, fontWeight: 600, color: "#1a1a1a" },
            }}
            secondaryTypographyProps={{ sx: { fontSize: 12 } }}
          />
          {plan && <PlanTag plan={plan} />}
        </MenuItem>
      );
    }),
    ...Array.from(ctx.inactiveTeamIds).map((firmId) => {
      const loaded = isFirmNameLoaded(firmId);
      const label = resolveFirmName(firmId);
      if (ctx.inactiveAdminTeamIds?.has(firmId)) {
        return (
          <MenuItem
            key={`reactivate-${firmId}`}
            onClick={() => reactivate(firmId)}
            sx={rowSx}
            data-testid={`reactivate-${firmId}`}
          >
            <WorkspaceAvatar color="grey.400" label={loaded ? label : null} />
            <ListItemText
              primary={loaded ? label : <Skeleton width={96} />}
              secondary={INACTIVE_ADMIN_ROW_SECONDARY}
              primaryTypographyProps={{
                noWrap: true,
                sx: { fontSize: 14, fontWeight: 600, color: "text.secondary" },
              }}
              secondaryTypographyProps={{ sx: { fontSize: 12, color: "warning.main" } }}
            />
          </MenuItem>
        );
      }
      return (
        <Tooltip
          key={`inactive-${firmId}`}
          title={INACTIVE_ROW_TOOLTIP}
          placement="right"
        >
          <span>
            <MenuItem disabled sx={{ ...rowSx, opacity: 1 }}>
              <WorkspaceAvatar color="grey.400" label={loaded ? label : null} />
              <ListItemText
                primary={loaded ? label : <Skeleton width={96} />}
                secondary={INACTIVE_ROW_SECONDARY}
                primaryTypographyProps={{
                  noWrap: true,
                  sx: { fontSize: 14, fontWeight: 600, color: "text.disabled" },
                }}
                secondaryTypographyProps={{
                  sx: { fontSize: 12, color: "warning.main" },
                }}
              />
            </MenuItem>
          </span>
        </Tooltip>
      );
    }),
    (activeIsTeam || canCreateTeam) && (
      <Divider key="divider" sx={{ my: 0.75 }} />
    ),
    // The team's home; the sidebar then drills into Members/Billing/Credits/Settings.
    activeIsTeam && (
      <MenuItem
        key="manage-team"
        onClick={() => {
          close();
          if (onBeforeNavigate && !onBeforeNavigate()) return;
          navigate("/home/team/members");
        }}
        sx={actionRowSx}
        data-testid="workspace-manage-team"
      >
        <SettingsIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 14 }}>{MANAGE_TEAM}</Typography>
      </MenuItem>
    ),
    canCreateTeam && (
      <MenuItem
        key="create-team"
        onClick={() => {
          close();
          teamAnalytics.createStarted("switcher");
          setShowSubscription("createTeam");
        }}
        sx={actionRowSx}
        data-testid="workspace-switcher-create-team"
      >
        <AddIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 14 }}>{CREATE_TEAM}</Typography>
      </MenuItem>
    ),
  ];

  return (
    <>
      {/* Phones get the avatar instead of "/ name" (the bar is full there). */}
      <Typography
        aria-hidden
        sx={{
          display: { xs: "none", sm: "block" },
          flexShrink: 0,
          color: "#d4d4d8",
          fontSize: 22,
          fontWeight: 300,
          userSelect: "none",
        }}
      >
        /
      </Typography>
      <ButtonBase
        ref={triggerRef}
        data-testid="workspace-switcher"
        aria-haspopup="menu"
        aria-expanded={open || undefined}
        aria-label={nameLoaded ? activeLabel : WORKSPACE_LOADING_LABEL}
        onClick={(e) => {
          // Drop last open's counts so rows read "Team" until fresh ones land.
          setMemberCounts({});
          setAnchorEl(e.currentTarget);
          // Opening the switcher means they've found it: no callout again.
          // It only counts as dismissing the callout if it was showing.
          if (permissionsSettled && hasActiveTeam) {
            markSwitcherTipSeen(
              uid,
              tipVisible ? "opened_switcher" : undefined,
            );
          }
        }}
        sx={{
          gap: 0.5,
          height: 32,
          px: { xs: 0.5, sm: 1.25 },
          minWidth: 0,
          borderRadius: "8px",
          backgroundColor: "#f4f4f5",
          color: "#1a1a1a",
          "&:hover": { backgroundColor: "#e9e9eb" },
          "&.Mui-focusVisible": {
            outline: "2px solid rgba(99, 102, 241, 0.5)",
          },
        }}
      >
        <WorkspaceAvatar
          color={workspaceColor(active)}
          label={nameLoaded ? activeLabel : null}
          personal={!activeIsTeam}
          size={24}
          sx={{ display: { xs: "flex", sm: "none" } }}
        />
        {nameLoaded ? (
          // minWidth 0: the name ellipsizes before the bar runs out of room.
          <Typography
            noWrap
            sx={{
              display: { xs: "none", sm: "block" },
              minWidth: 0,
              fontSize: 14,
              fontWeight: 600,
              maxWidth: 200,
            }}
          >
            {activeLabel}
          </Typography>
        ) : (
          <Skeleton
            variant="text"
            width={80}
            sx={{ display: { xs: "none", sm: "block" }, fontSize: 14 }}
          />
        )}
        <UnfoldMoreIcon
          sx={{ fontSize: 16, color: "#71717a", flexShrink: 0 }}
        />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ sx: { p: "6px" } }}
        slotProps={{ paper: { sx: menuPaperSx } }}
      >
        {items}
      </Menu>
      {/* Above the app bar, below modals (1300), so any dialog covers it. */}
      <Popper
        open={tipVisible}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 12] } },
          { name: "flip", enabled: false },
          { name: "preventOverflow", options: { padding: 16 } },
        ]}
        sx={{ zIndex: 1200 }}
      >
        <Paper
          role="dialog"
          aria-labelledby="workspace-switcher-tip-title"
          data-testid="workspace-switcher-tip"
          elevation={0}
          sx={tipPaperSx}
        >
          <Box sx={tipArrowSx} />
          <Typography
            id="workspace-switcher-tip-title"
            sx={{ fontSize: 14, fontWeight: 700 }}
          >
            {SWITCHER_TIP_TITLE}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "#d4d4d8" }}>
            {switcherTipBody(tipTeamName)}
          </Typography>
          <Stack direction="row" sx={{ mt: 1.5, justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() => markSwitcherTipSeen(uid, "got_it")}
              data-testid="workspace-switcher-tip-dismiss"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                bgcolor: "#fff",
                color: TIP_BG,
                "&:hover": { bgcolor: "#e4e4e7" },
              }}
            >
              {SWITCHER_TIP_DISMISS}
            </Button>
          </Stack>
        </Paper>
      </Popper>
    </>
  );
};
